import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import useAuthContext from "store/Auth/useAuthContext";

// Extend Window interface to include Pylon
declare global {
  interface Window {
    Pylon?: (command: string, data?: any) => void;
  }
}

/**
 * Hook to manage Pylon chat widget integration
 * - Shows widget on homepage routes (not dashboard)
 * - Automatically identifies authenticated users
 */
const usePylon = () => {
  const { pathname } = useLocation();
  const { userInfo } = useAuthContext();

  // Determine if we're on the homepage (not dashboard routes)
  const isHomepage = !pathname.startsWith("/beam") && !pathname.startsWith("/apps");

  useEffect(() => {
    if (!isHomepage) {
      // Hide widget on dashboard routes
      if (window.Pylon) {
        window.Pylon("hideChatBubble");
      }
      return;
    }

    // On homepage: show widget and identify user if authenticated
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total

    const checkPylon = setInterval(() => {
      attempts++;

      if (window.Pylon && typeof window.Pylon === "function") {
        clearInterval(checkPylon);

        // Show chat bubble on homepage
        window.Pylon("showChatBubble");

        // Configure user settings if authenticated
        if (userInfo?.identityUser?.email) {
          const { email, first_name, last_name } = userInfo.identityUser;
          const fullName = [first_name, last_name].filter(Boolean).join(" ");
          const token = JSON.parse(String(localStorage.getItem("authToken")));
          const env = process.env.REACT_APP_ENV;

          window.pylon = {
            chat_settings: {
              app_id: "b2c60b5c-4d86-4ca2-92d2-d9a3aa1df516",
              email: email,
              name: fullName || email,
              account_type: userInfo?.subscription?.subscription_type,
              auth_token: token,
              env: env,
            },
          };
        }
      }

      if (attempts >= maxAttempts) {
        clearInterval(checkPylon);
      }
    }, 100);

    return () => {
      clearInterval(checkPylon);
    };
  }, [isHomepage, userInfo]);
};

export default usePylon;
